import { ApiQueryParams } from '@frontend/api-utils';
import { ChartBinSize, ChartQueryParam, ChartQueryParams } from '@frontend/chart/types';
import { createContext, useContext } from 'react';

import { InsightEntityFilterKey } from './insight-filter-config';

export interface InsightDetailFiltersValue {
    /** True on the detail page when this insight supports filters. */
    active: boolean;
    showDateRange: boolean;
    after: string;
    before: string;
    binSize: ChartBinSize;
    lockBinSize: boolean;
    editableBinSizes: ChartBinSize[];
    binSizeUpgraded: boolean;
    rangeClamped: boolean;
    entityFilters: InsightEntityFilterKey[];
    spotId: string | null;
    productId: string | null;
    userId: string | null;
    iotType: string | null;
    iotBrand: string | null;
    /** ChartClient-ready entity params (only set keys). */
    entityParams: ApiQueryParams<ChartQueryParams>;
    setRange: (...args: [after: string, before: string]) => void;
    setBinSize: (...args: [binSize: ChartBinSize]) => void;
    setSpotId: (...args: [spotId: string | null]) => void;
    setProductId: (...args: [productId: string | null]) => void;
    setUserId: (...args: [userId: string | null]) => void;
    setIotType: (...args: [iotType: string | null]) => void;
    setIotBrand: (...args: [iotBrand: string | null]) => void;
}

export const buildInsightEntityParams = (args: {
    entityFilters: InsightEntityFilterKey[];
    spotId: string | null;
    productId: string | null;
    userId: string | null;
    iotType: string | null;
    iotBrand: string | null;
}): ApiQueryParams<ChartQueryParams> => {
    const params: ApiQueryParams<ChartQueryParams> = {};
    if (args.entityFilters.includes('spot') && args.spotId) {
        params[ChartQueryParam.SPOT_ID] = args.spotId;
    }
    if (args.entityFilters.includes('product') && args.productId) {
        params[ChartQueryParam.PRODUCT_ID] = args.productId;
    }
    if (args.entityFilters.includes('user') && args.userId) {
        params[ChartQueryParam.USER_ID] = args.userId;
    }
    if (args.entityFilters.includes('iotType') && args.iotType) {
        params[ChartQueryParam.TYPE] = args.iotType;
    }
    if (args.entityFilters.includes('iotBrand') && args.iotBrand) {
        params[ChartQueryParam.BRAND] = args.iotBrand;
    }
    return params;
};

const noopFilters: InsightDetailFiltersValue = {
    active: false,
    showDateRange: false,
    after: '',
    before: '',
    binSize: ChartBinSize.DAY,
    lockBinSize: true,
    editableBinSizes: [],
    binSizeUpgraded: false,
    rangeClamped: false,
    entityFilters: [],
    spotId: null,
    productId: null,
    userId: null,
    iotType: null,
    iotBrand: null,
    entityParams: {},
    setRange: () => undefined,
    setBinSize: () => undefined,
    setSpotId: () => undefined,
    setProductId: () => undefined,
    setUserId: () => undefined,
    setIotType: () => undefined,
    setIotBrand: () => undefined
};

export const InsightDetailFiltersContext = createContext<InsightDetailFiltersValue>(noopFilters);

export const useInsightDetailFilters = (): InsightDetailFiltersValue => useContext(InsightDetailFiltersContext);
